import React from 'react';
import { NewsProximityEvent } from '../hooks/useNewsProximity';

interface Props {
  isOpen: boolean;
  events: NewsProximityEvent[];
  orderAction: 'Buy' | 'Sell' | 'Flatten';
  onConfirm: () => void;
  onCancel: () => void;
}

const NewsOrderConfirmModal: React.FC<Props> = ({
  isOpen,
  events,
  orderAction,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen || events.length === 0) return null;

  return (
    <div style={styles.backdrop} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerIcon}>⚠️</span>
          <span style={styles.headerTitle}>News Event Warning</span>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <p style={styles.subtitle}>
            You are about to <strong>{orderAction}</strong> near a high-impact news event:
          </p>

          <div style={styles.eventList}>
            {events.map((ev) => (
              <div key={ev.id} style={styles.eventRow}>
                <span style={styles.eventCountry}>{ev.country}</span>
                <span style={styles.eventName}>{ev.event}</span>
                <span style={styles.eventTime}>
                  {ev.minutesUntil <= 1 ? '<1 min' : `${ev.minutesUntil} min`}
                </span>
              </div>
            ))}
          </div>

          <p style={styles.disclaimer}>
            High-impact news can cause extreme volatility and slippage.
          </p>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
          <button style={styles.confirmBtn} onClick={onConfirm}>
            Proceed Anyway
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.15s ease',
  },
  modal: {
    background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.98) 0%, rgba(30, 25, 15, 0.95) 100%)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(245, 158, 11, 0.35)',
    borderRadius: 16,
    boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 60px rgba(245,158,11,0.1)',
    width: 360,
    maxWidth: '90vw',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 18px',
    borderBottom: '1px solid rgba(245, 158, 11, 0.2)',
  },
  headerIcon: {
    fontSize: 18,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#f59e0b',
    letterSpacing: '0.02em',
  },
  body: {
    padding: '14px 18px',
  },
  subtitle: {
    fontSize: 12,
    color: '#d1d5db',
    margin: '0 0 12px 0',
    lineHeight: 1.5,
  },
  eventList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 12,
  },
  eventRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    background: 'rgba(245, 158, 11, 0.08)',
    borderRadius: 8,
    border: '1px solid rgba(245, 158, 11, 0.15)',
  },
  eventCountry: {
    fontSize: 11,
    fontWeight: 700,
    color: '#f59e0b',
    textTransform: 'uppercase' as const,
    minWidth: 28,
  },
  eventName: {
    fontSize: 12,
    color: '#e5e7eb',
    flex: 1,
  },
  eventTime: {
    fontSize: 11,
    fontWeight: 600,
    color: '#ef4444',
    whiteSpace: 'nowrap' as const,
  },
  disclaimer: {
    fontSize: 11,
    color: '#9ca3af',
    margin: 0,
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  actions: {
    display: 'flex',
    gap: 8,
    padding: '12px 18px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#d1d5db',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  confirmBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(245, 158, 11, 0.4)',
    background: 'rgba(245, 158, 11, 0.15)',
    color: '#f59e0b',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default NewsOrderConfirmModal;
